import React, { useState, useEffect } from 'react';
import { api } from '../../api.js';
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';
import FileThumbPicker from './FileThumbPicker.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';

const blank = {
  firstName: '', lastName: '', preferredName: '', dateOfBirth: '', sin: '',
  address: '', city: '', province: '', postalCode: '', country: 'Canada',
  phone: '', email: '', emergencyContact: '', emergencyContactPhone: '',
  position: '', department: '', employeeType: 'Payroll Employee', hireDate: '', status: 'Active',
  payType: '', payRate: '', payFrequency: '', td1FederalClaim: '', td1ProvincialClaim: '', cppEiExempt: false,
  w4FilingStatus: '', w4MultipleJobs: false, w4DependentsClaim: '', w4ExtraWithholding: '', stateFilingStatus: '', stateAllowances: '',
  businessName: '', businessNumber: '', gstHstNumber: '', lumpSumAmount: '', lumpSumFrequency: '',
  bankName: '', transitNumber: '', institutionNumber: '', bankAccount: '', notes: ''
};

function fromEmployee(e) {
  if (!e) return blank;
  return {
    firstName: e.first_name || '', lastName: e.last_name || '', preferredName: e.preferred_name || '',
    dateOfBirth: e.date_of_birth || '', sin: e.sin || '',
    address: e.address || '', city: e.city || '', province: e.province || '', postalCode: e.postal_code || '', country: e.country || 'Canada',
    phone: e.phone || '', email: e.email || '', emergencyContact: e.emergency_contact || '', emergencyContactPhone: e.emergency_contact_phone || '',
    position: e.position || '', department: e.department || '', employeeType: e.employee_type || 'Payroll Employee',
    hireDate: e.hire_date || '', status: e.status || 'Active',
    payType: e.pay_type || '', payRate: e.pay_rate ?? '', payFrequency: e.pay_frequency || '',
    td1FederalClaim: e.td1_federal_claim ?? '', td1ProvincialClaim: e.td1_provincial_claim ?? '', cppEiExempt: !!e.cpp_ei_exempt,
    w4FilingStatus: e.w4_filing_status || '', w4MultipleJobs: !!e.w4_multiple_jobs, w4DependentsClaim: e.w4_dependents_claim ?? '',
    w4ExtraWithholding: e.w4_extra_withholding ?? '', stateFilingStatus: e.state_filing_status || '', stateAllowances: e.state_allowances ?? '',
    businessName: e.business_name || '', businessNumber: e.business_number || '', gstHstNumber: e.gst_hst_number || '',
    lumpSumAmount: e.lump_sum_amount ?? '', lumpSumFrequency: e.lump_sum_frequency || '',
    bankName: e.bank_name || '', transitNumber: e.transit_number || '', institutionNumber: e.institution_number || '',
    bankAccount: e.bank_account || '', notes: e.notes || ''
  };
}

const TABS = ['Basic Information', 'Payroll & Tax', 'Documents'];

export default function EmployeeFormModal({ employee, onClose, onSaved }) {
  const [savedId, setSavedId] = useState(employee?.id || null);
  const [form, setForm] = useState(() => fromEmployee(employee));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [manualAddress, setManualAddress] = useState(false);
  const [photo, setPhoto] = useState(employee?.photo_url ? { url: employee.photo_url, fileName: 'Photo' } : null);
  const [companyCountry, setCompanyCountry] = useState('Canada');
  useEffect(() => { api('/settings').then(s => setCompanyCountry(s.country || 'Canada')).catch(() => {}); }, []);
  const isUS = companyCountry === 'United States';

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const isContractor = form.employeeType === 'Self-Employed Contractor';

  // ── Documents (tab 2) ────────────────────────────────────────────────────
  const [checklist, setChecklist] = useState(null);
  const loadChecklist = () => { if (savedId) api(`/dispatch/employees/${savedId}/checklist`).then(setChecklist).catch(() => setChecklist([])); };
  useEffect(() => { if (tab === 2 && savedId) loadChecklist(); }, [tab, savedId]);

  const toggleChecklistItem = async (item, isChecked) => {
    await api(`/dispatch/employees/${savedId}/checklist/${item.doc_type_id}`, { method: 'POST', body: { isChecked, expiryDate: item.expiry_date } });
    loadChecklist();
  };
  const setChecklistExpiry = async (item, expiryDate) => {
    await api(`/dispatch/employees/${savedId}/checklist/${item.doc_type_id}`, { method: 'POST', body: { isChecked: !!item.is_checked, expiryDate } });
    loadChecklist();
  };
  const addDocType = async () => {
    const name = window.prompt('New document type name');
    if (!name || !name.trim()) return;
    await api('/dispatch/employee-doc-types', { method: 'POST', body: { name, isRequired: false } });
    loadChecklist();
  };

  const persist = async () => {
    if (!form.firstName.trim()) { setError('First name is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const body = { ...form, photoUrl: photo?.url || '' };
      const saved = savedId
        ? await api(`/dispatch/employees/${savedId}`, { method: 'PATCH', body })
        : await api('/dispatch/employees', { method: 'POST', body });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { if (await persist()) onSaved(); };
  const saveAndNext = async () => { if (await persist()) setTab(t => Math.min(t + 1, TABS.length - 1)); };
  const reset = () => { setForm(employee ? fromEmployee(employee) : blank); setPhoto(employee?.photo_url ? { url: employee.photo_url, fileName: 'Photo' } : null); };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{employee ? 'Edit employee' : 'New employee'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>{t}</button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <label style={{ display: 'block', marginBottom: 18 }}>Photo<br/>
                <FileThumbPicker value={photo} onChange={setPhoto} label="Click to upload an image" />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>First Name<br/><input value={form.firstName} onChange={e => set('firstName', e.target.value)} /></label>
                <label>Last Name<br/><input value={form.lastName} onChange={e => set('lastName', e.target.value)} /></label>
                <label>Preferred Name<br/><input value={form.preferredName} onChange={e => set('preferredName', e.target.value)} /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Date of Birth<br/><input type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} /></label>
                <label>{isUS ? 'SSN (Social Security Number)' : 'SIN (Social Insurance Number)'}<br/><input value={form.sin} onChange={e => set('sin', e.target.value)} placeholder="000 000 000" /></label>
              </div>

              <label>Address<br/>
                <AddressAutocomplete value={form.address} onChange={val => set('address', val)}
                  onPlaceSelected={({ address, city, province, postalCode, country }) => {
                    setForm(f => ({ ...f, address, city, province, postalCode, country }));
                    setManualAddress(true);
                  }} />
              </label>
              <button type="button" className="link-toggle" style={{ marginBottom: manualAddress ? 12 : 18 }} onClick={() => setManualAddress(m => !m)}>
                {manualAddress ? 'Hide manual fields' : "Can't find it? Enter manually"}
              </button>
              {manualAddress && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>City<br/><input value={form.city} onChange={e => set('city', e.target.value)} /></label>
                  <label>Province / State<br/><input value={form.province} onChange={e => set('province', e.target.value)} /></label>
                  <label>Postal / Zip<br/><input value={form.postalCode} onChange={e => set('postalCode', e.target.value)} /></label>
                  <label>Country<br/>
                    <select value={form.country} onChange={e => set('country', e.target.value)}>
                      <option>Canada</option><option>United States</option>
                    </select>
                  </label>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Phone<br/><PhoneInput value={form.phone} onChange={v => set('phone', v)} /></label>
                <label>Email<br/><input value={form.email} onChange={e => set('email', e.target.value)} /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Emergency Contact<br/><input value={form.emergencyContact} onChange={e => set('emergencyContact', e.target.value)} /></label>
                <label>Emergency Contact Phone<br/><PhoneInput value={form.emergencyContactPhone} onChange={v => set('emergencyContactPhone', v)} /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Position / Job Title<br/><input value={form.position} onChange={e => set('position', e.target.value)} placeholder="e.g. Dispatcher, Yard Coordinator" /></label>
                <label>Department<br/>
                  <select value={form.department} onChange={e => set('department', e.target.value)}>
                    <option value="">—</option>
                    <option>Office</option><option>Yard</option><option>Warehouse</option><option>Dispatch</option><option>Accounting</option><option>Management</option><option>Driver</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Employee Type<br/>
                  <select value={form.employeeType} onChange={e => set('employeeType', e.target.value)}>
                    <option>Payroll Employee</option>
                    <option>Self-Employed Contractor</option>
                  </select>
                </label>
                <label>Hire Date<br/><input type="date" value={form.hireDate} onChange={e => set('hireDate', e.target.value)} /></label>
              </div>

              <label style={{ display: 'block', maxWidth: 320, marginBottom: 18 }}>Status<br/>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option>Active</option><option>On Leave</option><option>Terminated</option>
                </select>
              </label>

              <label style={{ display: 'block' }}>Notes<br/><input value={form.notes} onChange={e => set('notes', e.target.value)} /></label>
            </>
          )}

          {tab === 1 && (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 18, padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 6 }}>
                {isContractor
                  ? (isUS
                      ? "Self-employed contractor: paid a lump sum to their business. No FICA withheld — they remit their own taxes and receive a 1099 at year end, not a W-2."
                      : "Self-employed contractor: paid a lump sum to their incorporation. No CPP/EI withheld — they remit their own taxes and receive a T4A at year end, not a T4.")
                  : (isUS
                      ? "Payroll employee: standard W-2 employment. Federal and state income tax plus FICA (Social Security + Medicare) are withheld based on their W-4."
                      : "Payroll employee: standard T4 employment. CPP, EI, and income tax are withheld based on their TD1 claim amounts.")}
              </div>

              {!isContractor ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                    <label>Pay Type<br/>
                      <select value={form.payType} onChange={e => set('payType', e.target.value)}>
                        <option value="">—</option><option>Salary</option><option>Hourly</option>
                      </select>
                    </label>
                    <label>Pay Rate<br/><input type="number" value={form.payRate} onChange={e => set('payRate', e.target.value)} /></label>
                    <label>Pay Frequency<br/>
                      <select value={form.payFrequency} onChange={e => set('payFrequency', e.target.value)}>
                        <option value="">—</option><option>Weekly</option><option>Bi-weekly</option><option>Semi-monthly</option><option>Monthly</option>
                      </select>
                    </label>
                  </div>

                  {!isUS ? (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginBottom: 12 }}>TD1 TAX CREDIT CLAIM AMOUNTS</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                        <label>TD1 Federal Claim ($)<br/><input type="number" value={form.td1FederalClaim} onChange={e => set('td1FederalClaim', e.target.value)} /></label>
                        <label>TD1 Provincial Claim ($)<br/><input type="number" value={form.td1ProvincialClaim} onChange={e => set('td1ProvincialClaim', e.target.value)} /></label>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, marginBottom: 18 }}>
                        <input type="checkbox" checked={form.cppEiExempt} onChange={e => set('cppEiExempt', e.target.checked)} style={{ width: 'auto' }} />
                        CPP/EI Exempt (rare — only for specific exempt situations)
                      </label>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginBottom: 12 }}>FORM W-4 (FEDERAL)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                        <label>Filing Status<br/>
                          <select value={form.w4FilingStatus} onChange={e => set('w4FilingStatus', e.target.value)}>
                            <option value="">—</option>
                            <option>Single</option><option>Married Filing Jointly</option><option>Married Filing Separately</option><option>Head of Household</option>
                          </select>
                        </label>
                        <label>Dependents Claim Amount ($)<br/><input type="number" value={form.w4DependentsClaim} onChange={e => set('w4DependentsClaim', e.target.value)} /></label>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, marginBottom: 18 }}>
                        <input type="checkbox" checked={form.w4MultipleJobs} onChange={e => set('w4MultipleJobs', e.target.checked)} style={{ width: 'auto' }} />
                        Multiple Jobs / Spouse Works (Step 2 checkbox)
                      </label>
                      <label style={{ display: 'block', maxWidth: 280, marginBottom: 18 }}>Extra Withholding per Pay Period ($)<br/><input type="number" value={form.w4ExtraWithholding} onChange={e => set('w4ExtraWithholding', e.target.value)} /></label>

                      <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginBottom: 12 }}>STATE WITHHOLDING</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                        <label>State Filing Status<br/><input value={form.stateFilingStatus} onChange={e => set('stateFilingStatus', e.target.value)} placeholder="varies by state" /></label>
                        <label>State Allowances<br/><input type="number" value={form.stateAllowances} onChange={e => set('stateAllowances', e.target.value)} /></label>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, marginBottom: 18 }}>
                        <input type="checkbox" checked={form.cppEiExempt} onChange={e => set('cppEiExempt', e.target.checked)} style={{ width: 'auto' }} />
                        FICA Exempt (rare — only for specific exempt situations)
                      </label>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                    <label>Business / Incorporation Name<br/><input value={form.businessName} onChange={e => set('businessName', e.target.value)} /></label>
                    <label>{isUS ? 'EIN (Employer Identification Number)' : 'Business Number (BN)'}<br/><input value={form.businessNumber} onChange={e => set('businessNumber', e.target.value)} placeholder={isUS ? 'from the IRS' : '9 digits from CRA'} /></label>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isUS ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 18 }}>
                    {!isUS && <label>GST/HST Number<br/><input value={form.gstHstNumber} onChange={e => set('gstHstNumber', e.target.value)} /></label>}
                    <label>Lump Sum Amount ($)<br/><input type="number" value={form.lumpSumAmount} onChange={e => set('lumpSumAmount', e.target.value)} /></label>
                  </div>
                  <label style={{ display: 'block', maxWidth: 320, marginBottom: 18 }}>Payment Frequency<br/>
                    <select value={form.lumpSumFrequency} onChange={e => set('lumpSumFrequency', e.target.value)}>
                      <option value="">—</option><option>Weekly</option><option>Bi-weekly</option><option>Per Load</option><option>Monthly</option>
                    </select>
                  </label>
                </>
              )}

              <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '.04em', color: 'var(--text-dim)', marginBottom: 12 }}>DIRECT DEPOSIT</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
                <label>Bank Name<br/><input value={form.bankName} onChange={e => set('bankName', e.target.value)} /></label>
                <label>Transit Number<br/><input value={form.transitNumber} onChange={e => set('transitNumber', e.target.value)} /></label>
                <label>Institution Number<br/><input value={form.institutionNumber} onChange={e => set('institutionNumber', e.target.value)} /></label>
                <label>Account Number<br/><input value={form.bankAccount} onChange={e => set('bankAccount', e.target.value)} /></label>
              </div>
            </>
          )}

          {tab === 2 && (
            !savedId ? <div className="empty">Save the employee first to track document checklist items.</div>
            : checklist === null ? <div className="empty">Loading...</div>
            : (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14 }}>
                  Check a document once it's on file and set its expiry where relevant (e.g. work permits). This list is specific to employees.
                </div>
                {checklist.length === 0 && <div className="empty">No document types yet — add TD1 Federal, TD1 Provincial, SIN Document, Void Cheque, or anything else you need below.</div>}
                {checklist.map(item => (
                  <div className="checklist-row" key={item.doc_type_id}>
                    <input type="checkbox" checked={!!item.is_checked} style={{ width: 'auto' }} onChange={e => toggleChecklistItem(item, e.target.checked)} />
                    <div style={{ flex: 1 }}>{item.name}</div>
                    {!!item.is_required && <span className="checklist-req-badge">Required</span>}
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 700 }}>EXPIRY</span>
                    <input type="date" value={item.expiry_date || ''} style={{ width: 150 }} onChange={e => setChecklistExpiry(item, e.target.value)} />
                  </div>
                ))}
                <button type="button" onClick={addDocType} style={{ marginTop: 14 }}>+ Add document type</button>
              </>
            )
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : employee ? 'Save changes' : 'Create employee'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
